// Verbatim legal documents provided by the client (effective 25 August 2026).
export const termsMarkdown = `# Terms of Use

**Effective date: 25 August 2026**

These Terms of Use ("Terms") govern your access to and use of this CPF Seminar landing page, its registration forms, content, features and related services (collectively, the "Website"), as well as your registration for and participation in the seminar or related event (the "Event").

The Website and Event are organised by Affinity Group ("Affinity", "we", "us" or "our"), a representative group under Infinity Financial Advisory Pte. Ltd.

By accessing the Website, submitting a registration or attending the Event, you acknowledge that you have read and agree to these Terms. If you do not agree, please do not use the Website or register for the Event.

## 1. About the Website and Event

The Website provides information about a CPF retirement planning seminar and allows interested individuals to register, submit questions, receive Event information and access related educational materials.

The Event is intended for general education and discussion. Its content may include information concerning CPF accounts, retirement milestones, retirement sums, CPF LIFE, monthly payouts and general retirement planning considerations.

## 2. General Information Only

All content on the Website and at the Event is provided for general information and educational purposes only. It does not take into account your specific objectives, financial situation, needs or circumstances.

Nothing on the Website or presented at the Event constitutes or should be relied upon as:

- personalised financial, investment or insurance advice;
- legal, tax, accounting, medical or other professional advice;
- an offer, invitation, recommendation or solicitation to buy, sell or subscribe for any financial product or service;
- a guarantee of any financial, retirement or CPF-related outcome; or
- a substitute for information, statements or advice provided directly by the Central Provident Fund Board ("CPF Board") in relation to your CPF accounts or eligibility.

You should consider your own circumstances, verify information from official sources and, where appropriate, obtain advice from a suitably qualified professional before making any financial or retirement decision.

Attending the Event, using the Website or speaking with a presenter does not by itself create a financial adviser-client, legal, fiduciary or other professional relationship. Personalised financial advice, if requested, will only be provided separately by an appropriately authorised representative and subject to the applicable fact-finding, suitability, disclosure and compliance requirements.

## 3. CPF Information and Official Sources

CPF policies, retirement sums, payout ages, interest rates, eligibility requirements, terminology and other scheme details may change over time. Although we take reasonable care when preparing Event materials, we do not warrant that every item will remain current, complete or applicable to your circumstances.

You should verify CPF information through the official CPF Board website at [www.cpf.gov.sg](https://www.cpf.gov.sg/) or contact CPF Board directly before taking action.

This Website is not the CPF Board website. References to CPF Board, CPF, CPF LIFE or other CPF schemes are made for identification and educational purposes. The participation of a CPF Board representative, use of official information or appearance of a third-party speaker does not imply that CPF Board or any other organisation endorses Affinity Group, Infinity Financial Advisory Pte. Ltd., any representative, or any financial product or service.

## 4. Registration Eligibility and Information

You agree to provide complete, current and accurate information when registering or communicating with us. You must not register using another person's identity or contact details without that person's authorisation.

Unless otherwise stated, you must be at least 18 years old to register independently. If you are below 18, you must obtain the consent of a parent or legal guardian.

Submitting a form does not guarantee admission. A place is confirmed only when we issue a confirmation through the Website, email, SMS, WhatsApp or another stated channel. Registration may be subject to capacity, eligibility, verification, safety or operational requirements.

Complimentary registrations have no cash value and may not be sold. If you can no longer attend, please notify us so that the place may be offered to another participant.

## 5. Event Details, Changes and Cancellation

Event dates, times, venues, format, programme, speakers, topics, materials, capacity and other arrangements may change. We may postpone, relocate, convert, shorten or cancel the Event where reasonably necessary, including for operational, safety, regulatory, speaker-availability or circumstances beyond our reasonable control.

Where practicable, we will notify registered participants using the contact information provided. You are responsible for checking Event communications and ensuring that your contact details remain accurate.

To the extent permitted by law, we are not responsible for transport, accommodation, parking, childcare, loss of income or other personal expenses incurred in connection with a changed, postponed or cancelled complimentary Event.

## 6. Event Admission and Participant Conduct

You must behave respectfully and comply with reasonable instructions from the organisers, venue personnel and Event staff. You must not:

- disrupt the Event or interfere with another participant's experience;
- harass, threaten, discriminate against or behave abusively toward any person;
- engage in unauthorised promotion, solicitation, sales, recruitment or distribution of materials;
- record, reproduce or livestream the Event where recording has been prohibited;
- attempt to gain unauthorised access to restricted areas, systems or participant information; or
- engage in unlawful, unsafe or fraudulent conduct.

We may refuse admission or require a person to leave where we reasonably believe it is necessary for safety, security, Event integrity, venue requirements or the comfort of other participants. Where appropriate, serious conduct may be reported to the venue, relevant organisation or authorities.

## 7. Photography, Audio and Video Recording

Photography, audio recording or video recording may take place at the Event for documentation, educational, promotional or archival purposes. Where applicable, notices will be displayed or communicated, and consent will be obtained where required.

If you do not wish to appear in identifiable Event photography or recordings, please notify an Event team member upon arrival so that reasonable arrangements can be considered. Crowd or background images may be difficult to avoid completely in a public Event space.

You may not photograph or record another participant in a manner that infringes their privacy or other rights. Any recording you are permitted to make is for personal, non-commercial use only unless we provide written permission otherwise.

## 8. Communications and Marketing

By registering, you authorise us to send communications reasonably necessary to administer your registration, such as confirmations, reminders, venue instructions, schedule changes, calendar invitations, security notices and post-Event materials.

Operational Event communications are separate from marketing consent. We will only send marketing, advertising or promotional communications where permitted by law and in accordance with the consent or preferences you have provided. You may withdraw from marketing communications using the unsubscribe mechanism provided or by contacting us.

Further details are set out in our Privacy Policy.

## 9. Intellectual Property

Unless otherwise stated, the Website and Event materials—including text, graphics, photographs, videos, logos, designs, slides, downloads, recordings, software and presentation content—are owned by or licensed to Affinity Group, Infinity Financial Advisory Pte. Ltd., the relevant speaker or another identified rights holder and are protected by intellectual property laws.

You may access and use the content for your personal, non-commercial information only. You must not, without prior written permission:

- copy, reproduce, republish, upload, post, transmit or distribute substantial parts of the content;
- modify, translate, adapt, sell, license or create derivative works from the content;
- use content, names, logos or recordings for commercial, promotional or misleading purposes;
- remove copyright, trade mark or attribution notices; or
- imply endorsement, partnership or affiliation where none exists.

Nothing in these Terms transfers any intellectual property right to you.

## 10. Acceptable Use of the Website

You must use the Website only for lawful purposes. You must not:

- submit false, misleading, defamatory, offensive, unlawful or infringing content;
- introduce malicious code or attempt to damage, disable, overburden or impair the Website;
- bypass security measures, probe vulnerabilities or gain unauthorised access to any system or data;
- use automated tools to scrape, copy or extract Website content or personal data without permission;
- impersonate another person or misrepresent your affiliation; or
- use the Website in a way that infringes another person's rights or violates applicable law.

We may restrict or block access where we reasonably believe these Terms have been breached or the Website, Event or another person may be at risk.

## 11. Questions, Feedback and User Submissions

If you submit a question, comment, testimonial, survey response or other material, you confirm that you have the right to provide it and that it does not infringe another person's rights or contain unlawful material.

We may use questions and feedback to administer or improve the Event, respond to participants and identify common educational topics. We will not publicly attribute a submission to you without permission where attribution would identify you, unless disclosure is otherwise permitted or required by law.

Testimonials and participant experiences, where displayed, reflect individual views and circumstances. They do not guarantee that another person will have the same experience or achieve any particular outcome.

## 12. Third-Party Content, Links and Services

The Website may include links to, references to or embedded content from third parties, including CPF Board pages, mapping services, calendar providers, video platforms, social media services, analytics providers and Event partners.

Third-party services are provided under their own terms and privacy policies. We do not control and are not responsible for their availability, security, accuracy, content or practices. A link, reference or embed does not necessarily constitute our endorsement of that third party or all information provided by it.

You access and use third-party services at your own discretion and should review the applicable terms before doing so.

## 13. Website Availability and Security

We aim to keep the Website available and secure but do not guarantee uninterrupted, error-free or virus-free access. The Website may be unavailable due to maintenance, technical failure, service-provider disruption, cyber incident or circumstances beyond our control.

You are responsible for using suitable devices, software and security precautions when accessing the Website. Please notify us if you identify a suspected security issue, but do not attempt to exploit or publicly disclose a vulnerability in a manner that creates further risk.

## 14. Disclaimers

To the fullest extent permitted by law, the Website, Event and all related content are provided on an "as available" basis without any express or implied guarantee regarding availability, completeness, accuracy, timeliness, suitability, merchantability, fitness for a particular purpose or non-infringement.

We do not guarantee that:

- the Event will address every participant's circumstances or question;
- any speaker, session, topic or benefit will remain unchanged;
- participation will produce a particular financial, retirement, educational or personal result; or
- third-party content or services will be accurate, available or suitable.

Nothing in this section affects any right or remedy that cannot lawfully be excluded or restricted.

## 15. Limitation of Liability

To the fullest extent permitted by law, Affinity Group, Infinity Financial Advisory Pte. Ltd. and their respective representatives, officers, employees, speakers, contractors and Event partners will not be liable for indirect, incidental, special, exemplary or consequential loss arising from or relating to your use of the Website, participation in the Event, reliance on general information, or use of a third-party service.

You remain responsible for your own decisions and for verifying information before acting on it.

Nothing in these Terms excludes or restricts liability for fraud, fraudulent misrepresentation, death or personal injury caused by negligence, or any other liability that cannot lawfully be excluded or restricted. Any exclusion or limitation applies only to the extent that it is fair, reasonable and enforceable under applicable law.

## 16. Privacy and Personal Data

Our collection, use, disclosure and protection of personal data are governed by our Privacy Policy and applicable law. The Privacy Policy forms part of these Terms and should be read before you submit personal data.

## 17. Additional Terms and Regulatory Disclosures

Specific Event activities, promotions, downloads, appointments, products or services may be subject to additional terms, eligibility requirements or regulatory disclosures. Those additional terms will apply together with these Terms. If there is an inconsistency, the more specific terms will prevail for the relevant activity.

Where content refers to a financial product or service, you should read the applicable product documents and disclosures and seek advice regarding suitability before making a commitment. Any required regulatory or compliance statement displayed on the relevant page or material forms part of these Terms.

## 18. Changes to These Terms

We may update these Terms to reflect changes to the Website, Event, our practices or applicable requirements. The updated Terms will be published with a revised effective date.

Changes will apply from the date of publication and will not retrospectively remove rights that have already accrued. If a material change affects an existing registration, we may provide notice through an appropriate communication channel.

## 19. Severability, Waiver and Entire Agreement

If any provision of these Terms is held to be invalid, unlawful or unenforceable, that provision will be modified to the minimum extent necessary or treated as severed, and the remaining provisions will continue in effect.

Our failure or delay in enforcing a provision is not a waiver of our right to enforce it later.

These Terms, together with the Privacy Policy and any applicable specific Event terms or regulatory disclosures, constitute the agreement governing your use of the Website and participation in the Event.

## 20. Third-Party Rights

Except for Infinity Financial Advisory Pte. Ltd. and the persons expressly protected under Sections 14 and 15, a person who is not a party to these Terms has no right under the Contracts (Rights of Third Parties) Act 2001 to enforce any provision of these Terms.

## 21. Governing Law and Jurisdiction

These Terms are governed by the laws of Singapore. You agree to submit to the non-exclusive jurisdiction of the Singapore courts, without prejudice to any right you may have to use an applicable dispute-resolution or complaints process.

## 22. Contact Us

If you have questions about these Terms, please contact:

**Affinity Group**
A group representing Infinity Financial Advisory Pte. Ltd.
10 Eunos Road 8, #08-02 SingPost Centre, Singapore 408600
Email: admin@affinitygroup.sg
Telephone: +65 8077 4684
`;

export const privacyMarkdown = `# Privacy Policy

**Effective date: 25 August 2026**

This Privacy Policy explains how Affinity Group ("Affinity", "we", "us" or "our") collects, uses, discloses, protects and otherwise processes personal data in connection with this CPF Seminar landing page, our registration forms, the seminar and related communications.

Affinity Group operates as a representative group under Infinity Financial Advisory Pte. Ltd., a licensed financial advisory firm regulated by the Monetary Authority of Singapore.

We are committed to handling personal data in accordance with Singapore's Personal Data Protection Act 2012 ("PDPA"), the Do Not Call provisions and other applicable laws and regulatory requirements.

## 1. Personal Data We May Collect

Depending on how you interact with us, we may collect personal data such as:

- your full name;
- email address and telephone number;
- age or age range;
- seminar registration, attendance and participation details;
- your questions, areas of interest, feedback and communication preferences;
- correspondence and other information you provide when contacting us;
- photographs, audio recordings or video recordings captured at an in-person or virtual event, where you have been notified; and
- technical and usage information, such as your IP address, browser type, device information, referring page, pages viewed and interactions with the website.

You may choose not to provide certain personal data. However, we may then be unable to process your registration, provide event updates, respond to your enquiry or deliver a requested service.

Please do not submit another person's personal data unless you are authorised to do so and have informed that person of the purposes for which the information will be provided.

## 2. How We Collect Personal Data

We may collect personal data:

- when you complete or submit a registration, enquiry, feedback or appointment form;
- when you communicate with us by telephone, email, messaging application, social media or other channels;
- when you attend or participate in our seminars, webinars or related activities;
- from authorised representatives, referral partners or event partners, where permitted by law; and
- automatically through cookies, pixels, analytics tools and similar technologies when you use the website.

## 3. Purposes for Which We Use Personal Data

We may collect, use or disclose your personal data for purposes including:

- processing, administering and confirming your seminar registration;
- sending event details, reminders, updates, venue information, calendar invitations and post-event materials;
- verifying attendance and managing event capacity, security, logistics and participant support;
- responding to your questions, requests or feedback;
- contacting you regarding appointments, educational resources, financial planning topics, products or services that may be relevant to your expressed interests, where permitted and where the required consent has been obtained;
- facilitating follow-up by authorised representatives of Infinity Financial Advisory Pte. Ltd.;
- administering surveys, feedback exercises, promotions or future events;
- producing event recaps or publicity materials using photographs or recordings where appropriate notice and any required consent have been provided;
- operating, securing, maintaining and improving the website, forms, events and communications;
- conducting analytics, research, reporting, service quality reviews and internal business planning;
- detecting, investigating or preventing fraud, misuse, security incidents or unlawful activity;
- complying with applicable laws, regulatory requirements, requests from authorities and internal compliance procedures; and
- any other purpose that we notify you of at or before the time of collection.

We will not use your personal data for a materially different purpose without notifying you and obtaining consent where required.

## 4. Consent and Marketing Communications

Where consent is required, we will seek your consent before collecting, using or disclosing your personal data for the stated purposes. In some circumstances, consent may be deemed or an exception under the PDPA may apply.

If you separately consent to receive marketing, advertising or promotional communications, we may contact you by telephone call, SMS, email, WhatsApp or another channel you have provided. Such communications may relate to seminars, educational content, financial planning services, products or other activities offered by Affinity Group, Infinity Financial Advisory Pte. Ltd. or their authorised representatives.

We will comply with the Do Not Call provisions where applicable. You may opt out of marketing communications at any time by using the unsubscribe instructions in the relevant communication or by contacting us using the details in Section 17.

Withdrawal from marketing communications will not affect operational messages that are reasonably necessary to administer a registration, appointment, request or existing relationship.

## 5. Disclosure of Personal Data

We may disclose personal data, on a need-to-know basis, to:

- authorised employees, representatives and administrative personnel of Affinity Group;
- Infinity Financial Advisory Pte. Ltd. and its authorised representatives;
- event venues, organisers, partners, speakers or vendors where necessary to administer the seminar and where you have been appropriately notified;
- service providers supporting website hosting, form processing, customer relationship management, data storage, analytics, communications, email delivery, calendar invitations, event administration, IT support or security;
- professional advisers, auditors, insurers or compliance service providers;
- government agencies, regulators, courts, law enforcement bodies or other persons where disclosure is required or authorised by law; and
- parties involved in a proposed or actual business transfer, reorganisation, merger or acquisition, subject to appropriate confidentiality and data-protection safeguards.

We require service providers handling personal data on our behalf to use it only for the agreed purposes and to apply appropriate safeguards. We do not sell or rent your personal data to third parties.

## 6. CPF Board and Event Partners

This seminar may involve speakers, content contributors or supporting organisations. Unless expressly stated at the point of collection, registering for the seminar does not mean that your personal data will automatically be provided to the Central Provident Fund Board or every event partner.

If information needs to be shared with an event partner for a specific purpose, we will notify you and obtain consent where required.

## 7. Cookies and Similar Technologies

The website may use cookies, pixels, analytics tools and similar technologies to:

- enable essential website and form functionality;
- remember preferences;
- understand website traffic and user interactions;
- measure the effectiveness of advertisements or campaigns;
- improve website performance, content and user experience; and
- help prevent abuse or security incidents.

Some technologies may be provided by third-party analytics, advertising or hosting providers. These providers may process information in accordance with their own privacy policies.

You may control or disable cookies through your browser settings. Disabling certain cookies may affect the operation or functionality of the website. Where required, we will provide a cookie notice or obtain consent before placing non-essential cookies.

## 8. Transfer of Personal Data Outside Singapore

Some service providers or technology platforms may process or store personal data outside Singapore. Where personal data is transferred overseas, we will take reasonable steps to ensure that the recipient is bound by legally enforceable obligations to provide a standard of protection comparable to that required under the PDPA, or that another permitted transfer mechanism applies.

## 9. Accuracy of Personal Data

We take reasonable steps to ensure that personal data used or disclosed by us is accurate and complete where it is likely to be used to make a decision affecting you or disclosed to another organisation.

Please contact us if your personal data changes or if you believe the information we hold is inaccurate or incomplete.

## 10. Protection of Personal Data

We use reasonable administrative, physical and technical safeguards to protect personal data against unauthorised access, collection, use, disclosure, copying, modification, loss, disposal or similar risks.

However, no method of transmission or storage is completely secure. You should take appropriate precautions when transmitting personal data online and contact us promptly if you believe your information or communications with us may have been compromised.

## 11. Retention of Personal Data

We retain personal data only for as long as it is reasonably necessary to fulfil the purposes for which it was collected, to satisfy legitimate business or legal needs, or to comply with applicable regulatory and record-keeping requirements.

When personal data is no longer required, we will cease retaining it or remove the means by which it can be associated with particular individuals, where reasonably practicable and permitted by law.

## 12. Access and Correction

You may request access to personal data that we hold about you, information about how it has been used or disclosed within the applicable period, or correction of inaccurate or incomplete personal data.

Requests should be submitted in writing using the contact details in Section 17. We may need to verify your identity before processing a request. Requests are subject to the exceptions, procedures and timelines under the PDPA, and a reasonable fee may apply where permitted by law. If a fee applies, we will inform you before processing the request.

## 13. Withdrawal of Consent

You may withdraw consent to our collection, use or disclosure of your personal data by submitting a written request using the contact details in Section 17.

We will inform you of the likely consequences of withdrawal and process the request within a reasonable period. Withdrawal will not affect processing already carried out with valid consent or our continued processing where permitted or required by law. Depending on the nature of the request, we may be unable to continue providing certain services or administering an existing registration or request.

## 14. Third-Party Websites and Services

The website may contain links to third-party websites, video platforms, social media services, maps, calendar services or other external services. We are not responsible for the privacy, security or content practices of those third parties. You should review their applicable privacy policies before providing personal data or using their services.

## 15. Children and Minors

This seminar and website are not intended to solicit personal data from children. If you are below 18 years of age, please obtain the consent of your parent or legal guardian before submitting personal data. If we become aware that personal data was provided without the required authorisation, we will take reasonable steps to delete or otherwise address it.

## 16. Changes to This Privacy Policy

We may revise this Privacy Policy from time to time to reflect changes to our practices, technologies, legal obligations or regulatory requirements. The updated version will be published on this website with a revised effective date. Material changes may also be communicated through other appropriate channels.

## 17. Contact Us

For questions, requests, feedback or complaints concerning personal data, please contact:

**Data Protection Contact**
Affinity Group
A group representing Infinity Financial Advisory Pte. Ltd.
10 Eunos Road 8, #08-02 SingPost Centre, Singapore 408600
Email: admin@affinitygroup.sg
Telephone: +65 8077 4684

For withdrawal from marketing communications, you may also email **marketing@affinitygroup.sg**.

Please include sufficient information for us to identify you, understand your request and respond appropriately. We aim to address privacy-related concerns fairly and within a reasonable period.
`;
